import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Mail, Plus, Pencil, Trash2, ShieldAlert, Send, RefreshCw, Zap, Clock, Filter, Play, ChevronRight } from 'lucide-react';
import RichEmailEditor from '../components/RichEmailEditor';

// ★ 2026-08-11 (Fase 36) — Sezione "Email Automatiche" (Settings)
// CEO admin only (Richard/Eufemia/Morena/Claudia)

const STAGE_LABELS = {
  lead: 'Lead',
  chiamato: 'Chiamato',
  da_richiamare: 'Da richiamare',
  risposta: 'Risposta',
  appuntamento_fissato: 'Appuntamento',
  show: 'Show',
  pending_show: 'Pending',
  no_show: 'No-show',
  verification: 'Verification',
  proposta_inviata: 'Proposta inviata',
  proposta_ricevuta: 'Proposta ricevuta',
  chiuso_cliente: 'Chiuso cliente',
  chiuso_partner: 'Chiuso partner',
  perso: 'Perso',
};

const COUNTRY_OPTS = [
  { value: 'albania', label: 'Albania', flag: '🇦🇱' },
  { value: 'indonesia', label: 'Indonesia', flag: '🇮🇩' },
  { value: 'thailandia', label: 'Thailandia', flag: '🇹🇭' },
];

const PATH_OPTS = [
  { value: 'equity', label: 'Equity' },
  { value: 'real_estate', label: 'Real Estate' },
];

const TRIGGER_LABELS = {
  stage_change: 'Cambio stage',
  stage_inactivity: 'Inattività in stage',
  cluster_change: 'Cambio cluster HCF',
};

const DELAY_PRESETS = [
  { minutes: 0, label: 'Subito' },
  { minutes: 60, label: 'Dopo 1 ora' },
  { minutes: 6 * 60, label: 'Dopo 6 ore' },
  { minutes: 24 * 60, label: 'Dopo 1 giorno' },
  { minutes: 3 * 24 * 60, label: 'Dopo 3 giorni' },
  { minutes: 7 * 24 * 60, label: 'Dopo 7 giorni' },
];

const CEO_EMAILS = new Set([
  'legal@housecashflows.com',
  'eufemia@housecashflows.com',
  'morena@housecashflows.com',
  'claudia@housecashflows.com',
]);

const EMPTY_FORM = {
  name: '',
  active: true,
  trigger_type: 'stage_change',
  stage: 'appuntamento_fissato',
  inactivity_days: 3,
  target_cluster: 'P1',
  delay_minutes: 0,
  countries: [],
  paths: [],
  subject: '',
  body_html: '',
};

function TriggerIcon({ type }) {
  if (type === 'stage_inactivity') return <Clock className="w-3.5 h-3.5" />;
  if (type === 'cluster_change') return <Zap className="w-3.5 h-3.5" />;
  return <Play className="w-3.5 h-3.5" />;
}

export default function StageAutomationsPage() {
  const { user } = useAuth() || {};
  const isCeo = CEO_EMAILS.has((user?.email || '').toLowerCase());

  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [runsOpen, setRunsOpen] = useState(null);
  const [runs, setRuns] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/stage-automations');
      setItems(data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore caricamento automazioni');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    api.get('/stage-automations/_meta/stages')
      .then((r) => setMeta(r.data))
      .catch(() => setMeta(null));
  }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.trim().toLowerCase();
    return items.filter((it) =>
      (it.name || '').toLowerCase().includes(q)
      || (it.subject || '').toLowerCase().includes(q)
      || (STAGE_LABELS[it.stage] || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setEditorOpen(true);
    setTestEmail(user?.email || '');
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name || '',
      active: !!a.active,
      trigger_type: a.trigger_type || 'stage_change',
      stage: a.stage || 'appuntamento_fissato',
      inactivity_days: a.inactivity_days || 3,
      target_cluster: a.target_cluster || 'P1',
      delay_minutes: a.delay_minutes || 0,
      countries: a.countries || [],
      paths: a.paths || [],
      subject: a.subject || '',
      body_html: a.body_html || '',
    });
    setEditorOpen(true);
    setTestEmail(user?.email || '');
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Il nome è obbligatorio'); return; }
    if (!form.subject.trim()) { toast.error('L\'oggetto email è obbligatorio'); return; }
    if (!form.body_html.trim()) { toast.error('Il corpo email è obbligatorio'); return; }
    const payload = { ...form };
    // Pulizia campi non pertinenti al trigger scelto
    if (payload.trigger_type !== 'stage_inactivity') payload.inactivity_days = null;
    if (payload.trigger_type !== 'cluster_change') payload.target_cluster = null;
    if (payload.trigger_type === 'cluster_change') payload.stage = null;
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/stage-automations/${editing.id}`, payload);
        toast.success('Automazione aggiornata');
      } else {
        await api.post('/stage-automations', payload);
        toast.success('Automazione creata');
      }
      setEditorOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };

  const toggleActive = async (a) => {
    try {
      await api.patch(`/stage-automations/${a.id}`, { active: !a.active });
      toast.success(a.active ? 'Automazione disattivata' : 'Automazione attivata');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    }
  };

  const doDelete = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/stage-automations/${deleting.id}`);
      toast.success('Automazione eliminata');
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const doTestSend = async () => {
    if (!editing?.id) { toast.error('Salva prima l\'automazione'); return; }
    if (!testEmail.trim()) { toast.error('Inserisci un indirizzo email di test'); return; }
    setTestSending(true);
    try {
      const { data } = await api.post(`/stage-automations/${editing.id}/test-send`, { to_email: testEmail.trim() });
      if (data.provider_status === 'sent') {
        toast.success(`Test inviato a ${testEmail}`);
      } else {
        toast.warning(`Test registrato (Resend inattivo): ${data.provider_status}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore invio test');
    } finally { setTestSending(false); }
  };

  const showRuns = async (a) => {
    setRunsOpen(a);
    try {
      const { data } = await api.get(`/stage-automations/${a.id}/runs?limit=50`);
      setRuns(data.items || []);
    } catch (err) {
      toast.error('Errore caricamento storico');
    }
  };

  const toggleInArray = (arr, val) => arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val];

  const humanDelay = (min) => {
    if (!min || min === 0) return 'Subito';
    if (min < 60) return `${min}m`;
    if (min < 1440) return `${Math.round(min / 60)}h`;
    return `${Math.round(min / 1440)}g`;
  };

  if (!isCeo) {
    return (
      <div className="p-6" data-testid="stage-automations-forbidden">
        <Alert>
          <ShieldAlert className="w-4 h-4" />
          <AlertTitle>Accesso riservato</AlertTitle>
          <AlertDescription>
            Solo gli admin CEO (Richard, Eufemia, Morena, Claudia) possono gestire le Email Automatiche.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="stage-automations-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Mail className="w-6 h-6 text-[hsl(var(--primary))]" />
            <h1 className="text-2xl font-serif text-[hsl(var(--primary))]">Email Automatiche</h1>
            <Badge variant="outline" className="text-[10px]">{items.length} totali</Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Crea email che partono automaticamente quando un lead cambia stage, resta inattivo, o cambia cluster HCF.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="automations-refresh">
            <RefreshCw className="w-4 h-4 mr-1" />Aggiorna
          </Button>
          <Button size="sm" onClick={openCreate} data-testid="automations-new">
            <Plus className="w-4 h-4 mr-1" />Nuova
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-3">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome, oggetto, stage…"
            data-testid="automations-search"
          />
        </CardContent>
      </Card>

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => <Skeleton key={`sk-${i}`} className="h-24" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-10 text-center text-muted-foreground">
            <Mail className="w-10 h-10 mx-auto mb-3 opacity-30" />
            {items.length === 0
              ? 'Nessuna automazione ancora. Clicca "Nuova" per crearne una.'
              : 'Nessun risultato per il filtro corrente.'}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map((a) => (
            <Card key={a.id} className={`transition-shadow hover:shadow-md ${!a.active ? 'opacity-60' : ''}`} data-testid={`automation-card-${a.id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">{a.name}</div>
                    <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                      <TriggerIcon type={a.trigger_type} />
                      {TRIGGER_LABELS[a.trigger_type] || a.trigger_type}
                      {a.stage && <> · <b>{STAGE_LABELS[a.stage] || a.stage}</b></>}
                      {a.trigger_type === 'stage_inactivity' && a.inactivity_days && <> · {a.inactivity_days}g</>}
                      {a.trigger_type === 'cluster_change' && a.target_cluster && <> · {a.target_cluster}</>}
                    </div>
                  </div>
                  <Switch
                    checked={a.active}
                    onCheckedChange={() => toggleActive(a)}
                    data-testid={`automation-toggle-${a.id}`}
                  />
                </div>

                <div className="text-xs text-muted-foreground italic line-clamp-1">✉️ {a.subject}</div>

                <div className="flex items-center gap-1.5 flex-wrap">
                  <Badge variant="outline" className="text-[10px] gap-1">
                    <Clock className="w-3 h-3" />{humanDelay(a.delay_minutes)}
                  </Badge>
                  {(a.countries || []).length > 0 ? (
                    (a.countries).map((c) => {
                      const opt = COUNTRY_OPTS.find((x) => x.value === c);
                      return <Badge key={c} variant="outline" className="text-[10px]">{opt?.flag || ''} {opt?.label || c}</Badge>;
                    })
                  ) : (
                    <Badge variant="outline" className="text-[10px] text-muted-foreground">Tutti i paesi</Badge>
                  )}
                  {(a.paths || []).length > 0 ? (
                    (a.paths).map((p) => (
                      <Badge key={p} variant="outline" className="text-[10px] capitalize">{p.replace('_', ' ')}</Badge>
                    ))
                  ) : (
                    <Badge variant="outline" className="text-[10px] text-muted-foreground">Tutti i path</Badge>
                  )}
                </div>

                <div className="flex items-center gap-1 pt-2 border-t border-border/70">
                  <span className="text-[10px] text-muted-foreground">
                    {a.runs_sent || 0}/{a.runs_total || 0} inviate
                  </span>
                  <div className="flex-1" />
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => showRuns(a)} data-testid={`automation-history-${a.id}`}>
                    <ChevronRight className="w-3 h-3 mr-1" />Storico
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => openEdit(a)} data-testid={`automation-edit-${a.id}`}>
                    <Pencil className="w-3 h-3 mr-1" />Modifica
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-red-600 hover:text-red-700" onClick={() => setDeleting(a)} data-testid={`automation-delete-${a.id}`}>
                    <Trash2 className="w-3 h-3 mr-1" />Elimina
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Editor dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] p-0 flex flex-col" data-testid="automation-editor-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-border shrink-0">
            <DialogTitle>{editing ? 'Modifica automazione' : 'Nuova automazione email'}</DialogTitle>
            <DialogDescription>
              Definisci quando parte l&apos;email (trigger + delay), a chi (segmentazione) e il contenuto.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 px-6 py-4 overflow-y-auto flex-1">
            <div>
              <Label htmlFor="a-name" className="text-xs">Nome interno *</Label>
              <Input
                id="a-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Es. Welcome dopo appuntamento fissato"
                data-testid="automation-form-name"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Trigger *</Label>
                <Select value={form.trigger_type} onValueChange={(v) => setForm({ ...form, trigger_type: v })}>
                  <SelectTrigger data-testid="automation-form-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TRIGGER_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {(form.trigger_type === 'stage_change' || form.trigger_type === 'stage_inactivity') && (
                <div>
                  <Label className="text-xs">Stage *</Label>
                  <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                    <SelectTrigger data-testid="automation-form-stage"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(meta?.stages || Object.keys(STAGE_LABELS)).map((s) => (
                        <SelectItem key={s} value={s}>{STAGE_LABELS[s] || s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {form.trigger_type === 'stage_inactivity' && (
                <div>
                  <Label className="text-xs">Giorni di inattività *</Label>
                  <Input
                    type="number" min="1" max="365"
                    value={form.inactivity_days}
                    onChange={(e) => setForm({ ...form, inactivity_days: parseInt(e.target.value) || 1 })}
                    data-testid="automation-form-inactivity-days"
                  />
                </div>
              )}

              {form.trigger_type === 'cluster_change' && (
                <div>
                  <Label className="text-xs">Cluster HCF target *</Label>
                  <Select value={form.target_cluster} onValueChange={(v) => setForm({ ...form, target_cluster: v })}>
                    <SelectTrigger data-testid="automation-form-cluster"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {['P1', 'P2', 'P3', 'P4', 'P5'].map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label className="text-xs">Delay *</Label>
                <Select
                  value={String(form.delay_minutes)}
                  onValueChange={(v) => setForm({ ...form, delay_minutes: parseInt(v) })}
                >
                  <SelectTrigger data-testid="automation-form-delay"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DELAY_PRESETS.map((d) => (
                      <SelectItem key={d.minutes} value={String(d.minutes)}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t pt-3">
              <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2 flex items-center gap-1">
                <Filter className="w-3 h-3" />Segmentazione (opzionale)
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase">Countries</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {COUNTRY_OPTS.map((c) => (
                      <button
                        key={c.value}
                        type="button"
                        onClick={() => setForm({ ...form, countries: toggleInArray(form.countries, c.value) })}
                        className={`px-2 py-1 rounded-md border text-xs ${form.countries.includes(c.value) ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]' : 'bg-white border-border hover:bg-slate-50'}`}
                        data-testid={`automation-form-country-${c.value}`}
                      >
                        {c.flag} {c.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">Vuoto = tutti i paesi</p>
                </div>
                <div>
                  <Label className="text-[10px] uppercase">Paths</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {PATH_OPTS.map((p) => (
                      <button
                        key={p.value}
                        type="button"
                        onClick={() => setForm({ ...form, paths: toggleInArray(form.paths, p.value) })}
                        className={`px-2 py-1 rounded-md border text-xs ${form.paths.includes(p.value) ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]' : 'bg-white border-border hover:bg-slate-50'}`}
                        data-testid={`automation-form-path-${p.value}`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">Vuoto = tutti i path</p>
                </div>
              </div>
            </div>

            <div className="border-t pt-3">
              <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">Contenuto email</div>
              <div className="space-y-2">
                <div>
                  <Label className="text-xs">Oggetto *</Label>
                  <Input
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder="Es. Ciao {{lead_name}}, ecco i prossimi passi"
                    data-testid="automation-form-subject"
                  />
                </div>
                <div>
                  <Label className="text-xs">Corpo *</Label>
                  <RichEmailEditor
                    value={form.body_html}
                    onChange={(html) => setForm({ ...form, body_html: html })}
                    placeholder="Scrivi il corpo dell'email…"
                    minHeight="240px"
                    testId="automation-body-editor"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Placeholder: <code>{'{{lead_name}}'}</code>, <code>{'{{lead_email}}'}</code>, <code>{'{{lead_phone}}'}</code>, <code>{'{{stage}}'}</code>, <code>{'{{country}}'}</code>, <code>{'{{path}}'}</code>
                  </p>
                </div>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm border-t pt-3">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="automation-form-active" />
              Attiva subito dopo il salvataggio
            </label>

            {/* Test send box */}
            {editing?.id && (
              <div className="border-t pt-3">
                <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">Test invio</div>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label className="text-[10px] uppercase">Email destinatario di test</Label>
                    <Input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="tua@email.com"
                      className="h-9 mt-1"
                      data-testid="automation-test-email"
                    />
                  </div>
                  <Button
                    size="sm" variant="outline"
                    onClick={doTestSend}
                    disabled={testSending || !testEmail.trim()}
                    data-testid="automation-test-send-btn"
                  >
                    <Send className="w-4 h-4 mr-1" />
                    {testSending ? 'Invio…' : 'Invia test'}
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Manda una preview a te stesso con dati fittizi (Mario Rossi). Il subject viene prefissato con [TEST].
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-4 border-t border-border shrink-0 bg-[var(--surface-2)]/40">
            <Button variant="outline" onClick={() => setEditorOpen(false)} disabled={saving}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="automation-form-save">
              {saving ? 'Salvataggio…' : (editing ? 'Salva modifiche' : 'Crea automazione')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent data-testid="automation-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa automazione?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{deleting?.name}&quot; verrà eliminata definitivamente insieme al suo storico invii. L&apos;operazione è irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Runs history dialog */}
      <Dialog open={!!runsOpen} onOpenChange={(v) => !v && setRunsOpen(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col" data-testid="automation-runs-dialog">
          <DialogHeader>
            <DialogTitle>Storico invii — {runsOpen?.name}</DialogTitle>
            <DialogDescription>Ultimi 50 invii schedulati per questa automazione.</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1">
            {runs.length === 0 ? (
              <div className="text-center text-muted-foreground py-8 text-sm">Nessun invio registrato.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 border-b border-border text-left">
                  <tr>
                    <th className="px-2 py-1.5 text-[10px] uppercase">Schedulato</th>
                    <th className="px-2 py-1.5 text-[10px] uppercase">Lead</th>
                    <th className="px-2 py-1.5 text-[10px] uppercase">Status</th>
                    <th className="px-2 py-1.5 text-[10px] uppercase">Inviato</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.id} className="border-b border-border/50">
                      <td className="px-2 py-1.5 whitespace-nowrap">{r.scheduled_at?.slice(0, 16).replace('T', ' ')}</td>
                      <td className="px-2 py-1.5 truncate max-w-[180px]">{r.lead_name || r.lead_email || r.lead_id}</td>
                      <td className="px-2 py-1.5">
                        <Badge variant="outline" className={`text-[10px] ${r.status === 'sent' ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : r.status === 'failed' ? 'bg-red-50 border-red-300 text-red-800' : r.status === 'skipped' ? 'bg-slate-50 border-slate-300 text-slate-700' : 'bg-amber-50 border-amber-300 text-amber-800'}`}>
                          {r.status}
                        </Badge>
                      </td>
                      <td className="px-2 py-1.5 whitespace-nowrap">{r.sent_at?.slice(0, 16).replace('T', ' ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
